/**
 * adminDataStore — Supabase-first for photographers, API-first for others, mock fallback
 * Data source priority for photographers: Supabase > mock (DEMO MODE)
 * Data source priority for all other resources: REST API > mock (DEMO MODE)
 */

import {
  createContext, useCallback, useContext, useEffect, useState, type ReactNode,
} from "react";
import {
  servicesApi, serviceOptionsApi, portfolioApi, homepageSectionsApi,
  photographerAvailabilityApi, ordersApi,
  bookingConfirmationsApi, legalDocumentsApi,
  bookingCostItemsApi, bookingNotesApi, orderCancellationLogsApi, orderRescheduleLogsApi,
  isApiConfigured,
} from "../api/adminApiClient";
import {
  listPhotographers,
  createPhotographer,
  updatePhotographer,
  deactivatePhotographer,
} from "../api/photographersSupabaseApi";
import { isSupabaseConfigured } from "../lib/supabaseClient";
import {
  adaptServiceFromApi, adaptServiceToApi,
  adaptServiceOptionFromApi, adaptServiceOptionToApi,
  adaptPortfolioFromApi, adaptPortfolioToApi,
  adaptSectionFromApi, adaptSectionToApi,
  adaptAvailabilityFromApi, adaptAvailabilityToApi,
  adaptOrderFromApi, adaptOrderToApi,
  adaptConfirmationFromApi, adaptConfirmationToApi,
  adaptLegalDocFromApi, adaptLegalDocToApi,
  adaptCostItemFromApi, adaptCostItemToApi,
  adaptNoteFromApi, adaptNoteToApi,
  adaptCancellationLogFromApi, adaptCancellationLogToApi,
  adaptRescheduleLogFromApi, adaptRescheduleLogToApi,
} from "../api/adapters";
import type {
  CmsState, Service, ServiceOption, PortfolioItem, HomepageSection,
  Photographer, PhotographerAvailability, Order, BookingConfirmation, LegalDocument, SectionKey,
  BookingCostItem, BookingNote, OrderCancellationLog, OrderRescheduleLog,
} from "./types";
import { MOCK_SERVICES, MOCK_SERVICE_OPTIONS } from "../mocks/MOCK_admin_services";
import { MOCK_PORTFOLIO_ITEMS } from "../mocks/MOCK_admin_portfolio";
import { MOCK_HOMEPAGE_SECTIONS } from "../mocks/MOCK_admin_homepage";
import { MOCK_PHOTOGRAPHERS } from "../mocks/MOCK_admin_photographers";
import {
  MOCK_PHOTOGRAPHER_AVAILABILITY, MOCK_ORDERS,
  MOCK_BOOKING_CONFIRMATIONS, MOCK_LEGAL_DOCUMENTS,
  MOCK_BOOKING_COST_ITEMS, MOCK_BOOKING_NOTES,
  MOCK_ORDER_CANCELLATION_LOGS, MOCK_ORDER_RESCHEDULE_LOGS,
} from "../mocks/MOCK_admin_operations";

export interface ResourceStatus { loading: boolean; error: string | null; }

interface CmsContextValue {
  state: CmsState;
  status: {
    services: ResourceStatus; portfolio: ResourceStatus; sections: ResourceStatus;
    photographers: ResourceStatus; availability: ResourceStatus;
    orders: ResourceStatus; confirmations: ResourceStatus; legalDocs: ResourceStatus;
    costItems: ResourceStatus; bookingNotes: ResourceStatus;
    cancellationLogs: ResourceStatus; rescheduleLogs: ResourceStatus;
  };
  isApiMode: boolean;
  /** true when photographers are being served from Supabase (not mock) */
  isPhotographersSupabaseMode: boolean;
  refetchServices: () => Promise<void>;
  refetchPortfolio: () => Promise<void>;
  refetchSections: () => Promise<void>;
  refetchPhotographers: () => Promise<void>;
  refetchAvailability: () => Promise<void>;
  refetchOrders: () => Promise<void>;
  refetchConfirmations: () => Promise<void>;
  refetchLegalDocs: () => Promise<void>;
  refetchCostItems: () => Promise<void>;
  refetchBookingNotes: () => Promise<void>;
  refetchCancellationLogs: () => Promise<void>;
  refetchRescheduleLogs: () => Promise<void>;
  upsertService: (item: Service) => Promise<void>;
  removeService: (id: string) => Promise<void>;
  upsertServiceOption: (item: ServiceOption) => Promise<void>;
  removeServiceOption: (id: string) => Promise<void>;
  upsertPortfolioItem: (item: PortfolioItem) => Promise<void>;
  removePortfolioItem: (id: string) => Promise<void>;
  upsertHomepageSection: (item: HomepageSection) => Promise<void>;
  removeHomepageSection: (id: string) => Promise<void>;
  upsertPhotographer: (item: Photographer) => Promise<void>;
  removePhotographer: (id: string) => Promise<void>;
  upsertAvailability: (item: PhotographerAvailability) => Promise<void>;
  removeAvailability: (id: string) => Promise<void>;
  upsertOrder: (item: Order) => Promise<void>;
  removeOrder: (id: string) => Promise<void>;
  upsertConfirmation: (item: BookingConfirmation) => Promise<void>;
  removeConfirmation: (id: string) => Promise<void>;
  upsertLegalDoc: (item: LegalDocument) => Promise<void>;
  removeLegalDoc: (id: string) => Promise<void>;
  upsertCostItem: (item: BookingCostItem) => Promise<void>;
  removeCostItem: (id: string) => Promise<void>;
  upsertBookingNote: (item: BookingNote) => Promise<void>;
  removeBookingNote: (id: string) => Promise<void>;
  upsertCancellationLog: (item: OrderCancellationLog) => Promise<void>;
  removeCancellationLog: (id: string) => Promise<void>;
  upsertRescheduleLog: (item: OrderRescheduleLog) => Promise<void>;
  removeRescheduleLog: (id: string) => Promise<void>;
  resetToDefaults: () => void;
}

const CmsContext = createContext<CmsContextValue | null>(null);

const _supabaseMode = isSupabaseConfigured();

const MOCK_STATE: CmsState = {
  services: MOCK_SERVICES, serviceOptions: MOCK_SERVICE_OPTIONS,
  portfolioItems: MOCK_PORTFOLIO_ITEMS, homepageSections: MOCK_HOMEPAGE_SECTIONS,
  // ── photographers: empty when Supabase is live; mock only as fallback ──────
  photographers: _supabaseMode ? [] : MOCK_PHOTOGRAPHERS,
  photographerAvailability: MOCK_PHOTOGRAPHER_AVAILABILITY,
  orders: MOCK_ORDERS, bookingConfirmations: MOCK_BOOKING_CONFIRMATIONS,
  legalDocuments: MOCK_LEGAL_DOCUMENTS,
  bookingCostItems: MOCK_BOOKING_COST_ITEMS,
  bookingNotes: MOCK_BOOKING_NOTES,
  orderCancellationLogs: MOCK_ORDER_CANCELLATION_LOGS,
  orderRescheduleLogs: MOCK_ORDER_RESCHEDULE_LOGS,
};

const IDLE: ResourceStatus = { loading: false, error: null };
const LOADING: ResourceStatus = { loading: true, error: null };

export function AdminDataProvider({ children }: { children: ReactNode }) {
  const apiMode = isApiConfigured();
  const [state, setState] = useState<CmsState>(MOCK_STATE);
  const [svcStatus, setSvcStatus] = useState(IDLE);
  const [pfStatus, setPfStatus] = useState(IDLE);
  const [secStatus, setSecStatus] = useState(IDLE);
  const [phStatus, setPhStatus] = useState(IDLE);
  const [availStatus, setAvailStatus] = useState(IDLE);
  const [ordStatus, setOrdStatus] = useState(IDLE);
  const [confStatus, setConfStatus] = useState(IDLE);
  const [legalStatus, setLegalStatus] = useState(IDLE);
  const [costStatus, setCostStatus] = useState(IDLE);
  const [noteStatus, setNoteStatus] = useState(IDLE);
  const [cancelStatus, setCancelStatus] = useState(IDLE);
  const [reschedStatus, setReschedStatus] = useState(IDLE);

  // ── Shared local state helpers ───────────────────────────────────────────

  const localUpsert = useCallback(<T extends { id: string }>(key: keyof CmsState, item: T) => {
    setState((p) => {
      const list = p[key] as T[];
      const updated = list.some((x) => x.id === item.id)
        ? list.map((x) => x.id === item.id ? item : x)
        : [...list, item];
      return { ...p, [key]: updated };
    });
  }, []);

  const localRemove = useCallback((key: keyof CmsState, id: string) => {
    setState((p) => ({ ...p, [key]: (p[key] as { id: string }[]).filter((x) => x.id !== id) }));
  }, []);

  // ── Fetch functions ──────────────────────────────────────────────────────

  const fetchServices = useCallback(async () => {
    if (!apiMode) return;
    setSvcStatus(LOADING);
    try {
      const [svcs, opts] = await Promise.all([servicesApi.list(), serviceOptionsApi.list()]);
      setState((p) => ({
        ...p,
        services: (svcs as unknown[]).map(adaptServiceFromApi),
        serviceOptions: (opts as unknown[]).map(adaptServiceOptionFromApi),
      }));
      setSvcStatus(IDLE);
    } catch (e) { setSvcStatus({ loading: false, error: (e as Error).message }); }
  }, [apiMode]);

  const fetchPortfolio = useCallback(async () => {
    if (!apiMode) return;
    setPfStatus(LOADING);
    try {
      const items = await portfolioApi.list();
      setState((p) => ({ ...p, portfolioItems: (items as unknown[]).map(adaptPortfolioFromApi) }));
      setPfStatus(IDLE);
    } catch (e) { setPfStatus({ loading: false, error: (e as Error).message }); }
  }, [apiMode]);

  const fetchSections = useCallback(async () => {
    if (!apiMode) return;
    setSecStatus(LOADING);
    try {
      const items = await homepageSectionsApi.list();
      setState((p) => ({ ...p, homepageSections: (items as unknown[]).map(adaptSectionFromApi) }));
      setSecStatus(IDLE);
    } catch (e) { setSecStatus({ loading: false, error: (e as Error).message }); }
  }, [apiMode]);

  // ── photographers: Supabase-first ─────────────────────────────────────────
  const fetchPhotographers = useCallback(async () => {
    if (!_supabaseMode) {
      if (import.meta.env.DEV) console.info("[TaBiFoTo] Supabase not configured — photographers using mock data");
      return;
    }
    setPhStatus(LOADING);
    try {
      const items = await listPhotographers();
      setState((p) => ({ ...p, photographers: items }));
      setPhStatus(IDLE);
    } catch (e) {
      setPhStatus({ loading: false, error: (e as Error).message });
      if (import.meta.env.DEV) console.error("[photographers] fetch error:", e);
    }
  }, []);

  const fetchAvailability = useCallback(async () => {
    if (!apiMode) return;
    setAvailStatus(LOADING);
    try {
      const items = await photographerAvailabilityApi.list();
      setState((p) => ({ ...p, photographerAvailability: (items as unknown[]).map(adaptAvailabilityFromApi) }));
      setAvailStatus(IDLE);
    } catch (e) { setAvailStatus({ loading: false, error: (e as Error).message }); }
  }, [apiMode]);

  const fetchOrders = useCallback(async () => {
    if (!apiMode) return;
    setOrdStatus(LOADING);
    try {
      const items = await ordersApi.list();
      setState((p) => ({ ...p, orders: (items as unknown[]).map(adaptOrderFromApi) }));
      setOrdStatus(IDLE);
    } catch (e) { setOrdStatus({ loading: false, error: (e as Error).message }); }
  }, [apiMode]);

  const fetchConfirmations = useCallback(async () => {
    if (!apiMode) return;
    setConfStatus(LOADING);
    try {
      const items = await bookingConfirmationsApi.list();
      setState((p) => ({ ...p, bookingConfirmations: (items as unknown[]).map(adaptConfirmationFromApi) }));
      setConfStatus(IDLE);
    } catch (e) { setConfStatus({ loading: false, error: (e as Error).message }); }
  }, [apiMode]);

  const fetchLegalDocs = useCallback(async () => {
    if (!apiMode) return;
    setLegalStatus(LOADING);
    try {
      const items = await legalDocumentsApi.list();
      setState((p) => ({ ...p, legalDocuments: (items as unknown[]).map(adaptLegalDocFromApi) }));
      setLegalStatus(IDLE);
    } catch (e) { setLegalStatus({ loading: false, error: (e as Error).message }); }
  }, [apiMode]);

  const fetchCostItems = useCallback(async () => {
    if (!apiMode) return;
    setCostStatus(LOADING);
    try {
      const items = await bookingCostItemsApi.list();
      setState((p) => ({ ...p, bookingCostItems: (items as unknown[]).map(adaptCostItemFromApi) }));
      setCostStatus(IDLE);
    } catch (e) { setCostStatus({ loading: false, error: (e as Error).message }); }
  }, [apiMode]);

  const fetchBookingNotes = useCallback(async () => {
    if (!apiMode) return;
    setNoteStatus(LOADING);
    try {
      const items = await bookingNotesApi.list();
      setState((p) => ({ ...p, bookingNotes: (items as unknown[]).map(adaptNoteFromApi) }));
      setNoteStatus(IDLE);
    } catch (e) { setNoteStatus({ loading: false, error: (e as Error).message }); }
  }, [apiMode]);

  const fetchCancellationLogs = useCallback(async () => {
    if (!apiMode) return;
    setCancelStatus(LOADING);
    try {
      const items = await orderCancellationLogsApi.list();
      setState((p) => ({ ...p, orderCancellationLogs: (items as unknown[]).map(adaptCancellationLogFromApi) }));
      setCancelStatus(IDLE);
    } catch (e) { setCancelStatus({ loading: false, error: (e as Error).message }); }
  }, [apiMode]);

  const fetchRescheduleLogs = useCallback(async () => {
    if (!apiMode) return;
    setReschedStatus(LOADING);
    try {
      const items = await orderRescheduleLogsApi.list();
      setState((p) => ({ ...p, orderRescheduleLogs: (items as unknown[]).map(adaptRescheduleLogFromApi) }));
      setReschedStatus(IDLE);
    } catch (e) { setReschedStatus({ loading: false, error: (e as Error).message }); }
  }, [apiMode]);

  useEffect(() => {
    // photographers always fetches from Supabase if configured
    void fetchPhotographers();

    if (!apiMode) {
      if (import.meta.env.DEV) console.info("[TaBiFoTo] REST API not configured — other resources using mock data");
      return;
    }
    void fetchServices(); void fetchPortfolio(); void fetchSections();
    void fetchAvailability();
    void fetchOrders(); void fetchConfirmations(); void fetchLegalDocs();
    void fetchCostItems(); void fetchBookingNotes();
    void fetchCancellationLogs(); void fetchRescheduleLogs();
  }, [apiMode, fetchServices, fetchPortfolio, fetchSections, fetchPhotographers,
      fetchAvailability, fetchOrders, fetchConfirmations, fetchLegalDocs,
      fetchCostItems, fetchBookingNotes, fetchCancellationLogs, fetchRescheduleLogs]);

  // ── Mutations ────────────────────────────────────────────────────────────

  const upsertService = useCallback(async (item: Service) => {
    localUpsert("services", item);
    if (!apiMode) return;
    const isNew = !state.services.some((s) => s.id === item.id);
    const r = isNew ? await servicesApi.create(adaptServiceToApi(item)) : await servicesApi.update(item.id, adaptServiceToApi(item));
    localUpsert("services", adaptServiceFromApi(r));
  }, [apiMode, localUpsert, state.services]);

  const removeService = useCallback(async (id: string) => {
    localRemove("services", id);
    if (apiMode) await servicesApi.deactivate(id);
  }, [apiMode, localRemove]);

  const upsertServiceOption = useCallback(async (item: ServiceOption) => {
    localUpsert("serviceOptions", item);
    if (!apiMode) return;
    const isNew = !state.serviceOptions.some((o) => o.id === item.id);
    const r = isNew ? await serviceOptionsApi.create(adaptServiceOptionToApi(item)) : await serviceOptionsApi.update(item.id, adaptServiceOptionToApi(item));
    localUpsert("serviceOptions", adaptServiceOptionFromApi(r));
  }, [apiMode, localUpsert, state.serviceOptions]);

  const removeServiceOption = useCallback(async (id: string) => {
    localRemove("serviceOptions", id);
    if (apiMode) await serviceOptionsApi.remove(id);
  }, [apiMode, localRemove]);

  const upsertPortfolioItem = useCallback(async (item: PortfolioItem) => {
    localUpsert("portfolioItems", item);
    if (!apiMode) return;
    const isNew = !state.portfolioItems.some((p) => p.id === item.id);
    const r = isNew ? await portfolioApi.create(adaptPortfolioToApi(item)) : await portfolioApi.update(item.id, adaptPortfolioToApi(item));
    localUpsert("portfolioItems", adaptPortfolioFromApi(r));
  }, [apiMode, localUpsert, state.portfolioItems]);

  const removePortfolioItem = useCallback(async (id: string) => {
    localRemove("portfolioItems", id);
    if (apiMode) await portfolioApi.remove(id);
  }, [apiMode, localRemove]);

  const upsertHomepageSection = useCallback(async (item: HomepageSection) => {
    localUpsert("homepageSections", item);
    if (!apiMode) return;
    const isNew = !state.homepageSections.some((h) => h.id === item.id);
    const r = isNew ? await homepageSectionsApi.create(adaptSectionToApi(item)) : await homepageSectionsApi.update(item.id, adaptSectionToApi(item));
    localUpsert("homepageSections", adaptSectionFromApi(r));
  }, [apiMode, localUpsert, state.homepageSections]);

  const removeHomepageSection = useCallback(async (id: string) => {
    localRemove("homepageSections", id);
    if (apiMode) await homepageSectionsApi.remove(id);
  }, [apiMode, localRemove]);

  // ── photographers: Supabase mutations ─────────────────────────────────────
  const upsertPhotographer = useCallback(async (item: Photographer) => {
    if (!_supabaseMode) {
      // DEMO MODE: local state only
      localUpsert("photographers", item);
      return;
    }
    const isNew = !state.photographers.some((p) => p.id === item.id);
    try {
      const saved = isNew
        ? await createPhotographer({
            photographer_code: item.photographer_code,
            display_name: item.display_name,
            legal_name: item.legal_name,
            email: item.email,
            phone: item.phone,
            country_code: item.country_code,
            city_base: item.city_base,
            languages: item.languages,
            status: item.status,
            portfolio_url: item.portfolio_url,
            notes: item.notes,
            is_active: item.is_active,
          })
        : await updatePhotographer(item.id, {
            photographer_code: item.photographer_code,
            display_name: item.display_name,
            legal_name: item.legal_name,
            email: item.email,
            phone: item.phone,
            country_code: item.country_code,
            city_base: item.city_base,
            languages: item.languages,
            status: item.status,
            portfolio_url: item.portfolio_url,
            notes: item.notes,
            is_active: item.is_active,
          });
      localUpsert("photographers", saved);
    } catch (e) {
      if (import.meta.env.DEV) console.error("[photographers] upsert error:", e);
      throw e;
    }
  }, [localUpsert, state.photographers]);

  const removePhotographer = useCallback(async (id: string) => {
    if (!_supabaseMode) {
      localRemove("photographers", id);
      return;
    }
    try {
      const updated = await deactivatePhotographer(id);
      localUpsert("photographers", updated);
    } catch (e) {
      if (import.meta.env.DEV) console.error("[photographers] deactivate error:", e);
      throw e;
    }
  }, [localRemove, localUpsert]);

  const upsertAvailability = useCallback(async (item: PhotographerAvailability) => {
    localUpsert("photographerAvailability", item);
    if (!apiMode) return;
    const isNew = !state.photographerAvailability.some((a) => a.id === item.id);
    const r = isNew ? await photographerAvailabilityApi.create(adaptAvailabilityToApi(item)) : await photographerAvailabilityApi.update(item.id, adaptAvailabilityToApi(item));
    localUpsert("photographerAvailability", adaptAvailabilityFromApi(r));
  }, [apiMode, localUpsert, state.photographerAvailability]);

  const removeAvailability = useCallback(async (id: string) => {
    localRemove("photographerAvailability", id);
    if (apiMode) await photographerAvailabilityApi.remove(id);
  }, [apiMode, localRemove]);

  const upsertOrder = useCallback(async (item: Order) => {
    localUpsert("orders", item);
    if (!apiMode) return;
    const isNew = !state.orders.some((o) => o.id === item.id);
    const r = isNew ? await ordersApi.create(adaptOrderToApi(item)) : await ordersApi.update(item.id, adaptOrderToApi(item));
    localUpsert("orders", adaptOrderFromApi(r));
  }, [apiMode, localUpsert, state.orders]);

  const removeOrder = useCallback(async (id: string) => {
    localRemove("orders", id);
    if (apiMode) await ordersApi.remove(id);
  }, [apiMode, localRemove]);

  const upsertConfirmation = useCallback(async (item: BookingConfirmation) => {
    localUpsert("bookingConfirmations", item);
    if (!apiMode) return;
    const isNew = !state.bookingConfirmations.some((c) => c.id === item.id);
    const r = isNew ? await bookingConfirmationsApi.create(adaptConfirmationToApi(item)) : await bookingConfirmationsApi.update(item.id, adaptConfirmationToApi(item));
    localUpsert("bookingConfirmations", adaptConfirmationFromApi(r));
  }, [apiMode, localUpsert, state.bookingConfirmations]);

  const removeConfirmation = useCallback(async (id: string) => {
    localRemove("bookingConfirmations", id);
    if (apiMode) await bookingConfirmationsApi.remove(id);
  }, [apiMode, localRemove]);

  const upsertLegalDoc = useCallback(async (item: LegalDocument) => {
    localUpsert("legalDocuments", item);
    if (!apiMode) return;
    const isNew = !state.legalDocuments.some((d) => d.id === item.id);
    const r = isNew ? await legalDocumentsApi.create(adaptLegalDocToApi(item)) : await legalDocumentsApi.update(item.id, adaptLegalDocToApi(item));
    localUpsert("legalDocuments", adaptLegalDocFromApi(r));
  }, [apiMode, localUpsert, state.legalDocuments]);

  const removeLegalDoc = useCallback(async (id: string) => {
    localRemove("legalDocuments", id);
    if (apiMode) await legalDocumentsApi.remove(id);
  }, [apiMode, localRemove]);

  const upsertCostItem = useCallback(async (item: BookingCostItem) => {
    localUpsert("bookingCostItems", item);
    if (!apiMode) return;
    const isNew = !state.bookingCostItems.some((c) => c.id === item.id);
    const r = isNew ? await bookingCostItemsApi.create(adaptCostItemToApi(item)) : await bookingCostItemsApi.update(item.id, adaptCostItemToApi(item));
    localUpsert("bookingCostItems", adaptCostItemFromApi(r));
  }, [apiMode, localUpsert, state.bookingCostItems]);

  const removeCostItem = useCallback(async (id: string) => {
    localRemove("bookingCostItems", id);
    if (apiMode) await bookingCostItemsApi.remove(id);
  }, [apiMode, localRemove]);

  const upsertBookingNote = useCallback(async (item: BookingNote) => {
    localUpsert("bookingNotes", item);
    if (!apiMode) return;
    const isNew = !state.bookingNotes.some((n) => n.id === item.id);
    if (isNew) {
      const r = await bookingNotesApi.create(adaptNoteToApi(item));
      localUpsert("bookingNotes", adaptNoteFromApi(r));
    }
  }, [apiMode, localUpsert, state.bookingNotes]);

  const removeBookingNote = useCallback(async (id: string) => {
    localRemove("bookingNotes", id);
    if (apiMode) await bookingNotesApi.remove(id);
  }, [apiMode, localRemove]);

  const upsertCancellationLog = useCallback(async (item: OrderCancellationLog) => {
    localUpsert("orderCancellationLogs", item);
    if (!apiMode) return;
    const isNew = !state.orderCancellationLogs.some((l) => l.id === item.id);
    const r = isNew ? await orderCancellationLogsApi.create(adaptCancellationLogToApi(item)) : await orderCancellationLogsApi.update(item.id, adaptCancellationLogToApi(item));
    localUpsert("orderCancellationLogs", adaptCancellationLogFromApi(r));
  }, [apiMode, localUpsert, state.orderCancellationLogs]);

  const removeCancellationLog = useCallback(async (id: string) => {
    localRemove("orderCancellationLogs", id);
    if (apiMode) await orderCancellationLogsApi.remove(id);
  }, [apiMode, localRemove]);

  const upsertRescheduleLog = useCallback(async (item: OrderRescheduleLog) => {
    localUpsert("orderRescheduleLogs", item);
    if (!apiMode) return;
    const isNew = !state.orderRescheduleLogs.some((l) => l.id === item.id);
    const r = isNew ? await orderRescheduleLogsApi.create(adaptRescheduleLogToApi(item)) : await orderRescheduleLogsApi.update(item.id, adaptRescheduleLogToApi(item));
    localUpsert("orderRescheduleLogs", adaptRescheduleLogFromApi(r));
  }, [apiMode, localUpsert, state.orderRescheduleLogs]);

  const removeRescheduleLog = useCallback(async (id: string) => {
    localRemove("orderRescheduleLogs", id);
    if (apiMode) await orderRescheduleLogsApi.remove(id);
  }, [apiMode, localRemove]);

  const resetToDefaults = useCallback(() => setState(MOCK_STATE), []);

  return (
    <CmsContext.Provider value={{
      state, isApiMode: apiMode,
      isPhotographersSupabaseMode: _supabaseMode,
      status: {
        services: svcStatus, portfolio: pfStatus, sections: secStatus,
        photographers: phStatus, availability: availStatus,
        orders: ordStatus, confirmations: confStatus, legalDocs: legalStatus,
        costItems: costStatus, bookingNotes: noteStatus,
        cancellationLogs: cancelStatus, rescheduleLogs: reschedStatus,
      },
      refetchServices: fetchServices, refetchPortfolio: fetchPortfolio,
      refetchSections: fetchSections, refetchPhotographers: fetchPhotographers,
      refetchAvailability: fetchAvailability, refetchOrders: fetchOrders,
      refetchConfirmations: fetchConfirmations, refetchLegalDocs: fetchLegalDocs,
      refetchCostItems: fetchCostItems, refetchBookingNotes: fetchBookingNotes,
      refetchCancellationLogs: fetchCancellationLogs, refetchRescheduleLogs: fetchRescheduleLogs,
      upsertService, removeService, upsertServiceOption, removeServiceOption,
      upsertPortfolioItem, removePortfolioItem, upsertHomepageSection, removeHomepageSection,
      upsertPhotographer, removePhotographer, upsertAvailability, removeAvailability,
      upsertOrder, removeOrder, upsertConfirmation, removeConfirmation,
      upsertLegalDoc, removeLegalDoc,
      upsertCostItem, removeCostItem, upsertBookingNote, removeBookingNote,
      upsertCancellationLog, removeCancellationLog, upsertRescheduleLog, removeRescheduleLog,
      resetToDefaults,
    }}>
      {children}
    </CmsContext.Provider>
  );
}

export function useAdminData() {
  const ctx = useContext(CmsContext);
  if (!ctx) throw new Error("useAdminData must be used within AdminDataProvider");
  return ctx;
}

// ─── Frontend selectors ───────────────────────────────────────────────────────

export function useActiveServices() {
  const { state } = useAdminData();
  return [...state.services].filter((s) => s.is_active).sort((a, b) => a.sort_order - b.sort_order);
}

export function useAvailableCities() {
  const { state } = useAdminData();
  return new Set(state.services.filter((s) => s.is_active).flatMap((s) => s.city_scope));
}

export function useServicesByCity(city: string) {
  const { state } = useAdminData();
  return [...state.services].filter((s) => s.is_active && s.city_scope.includes(city)).sort((a, b) => a.sort_order - b.sort_order);
}

export function useShowreelItems() {
  const { state } = useAdminData();
  return [...state.portfolioItems].filter((p) => p.is_showreel && p.is_published).sort((a, b) => a.sort_order - b.sort_order);
}

export function useFeaturedItems() {
  const { state } = useAdminData();
  return [...state.portfolioItems].filter((p) => p.is_featured && p.is_published).sort((a, b) => a.sort_order - b.sort_order);
}

export function usePublishedPortfolio() {
  const { state } = useAdminData();
  return [...state.portfolioItems].filter((p) => p.is_published).sort((a, b) => a.sort_order - b.sort_order);
}

export function useHomepageSection(sectionKey: SectionKey) {
  const { state } = useAdminData();
  return [...state.homepageSections].filter((h) => h.section_key === sectionKey && h.is_active).sort((a, b) => a.sort_order - b.sort_order)[0] ?? null;
}

export function useHomepageSections(sectionKey: SectionKey) {
  const { state } = useAdminData();
  return [...state.homepageSections].filter((h) => h.section_key === sectionKey && h.is_active).sort((a, b) => a.sort_order - b.sort_order);
}

export function useServiceOptions(serviceId: string) {
  const { state } = useAdminData();
  return [...state.serviceOptions].filter((o) => o.service_id === serviceId && o.is_active).sort((a, b) => a.sort_order - b.sort_order);
}

export function useActivePhotographers() {
  const { state } = useAdminData();
  return [...state.photographers].filter((p) => p.is_active).sort((a, b) => a.display_name.localeCompare(b.display_name));
}

export function usePhotographerById(id: string) {
  const { state } = useAdminData();
  return state.photographers.find((p) => p.id === id) ?? null;
}

export function useOrderById(id: string) {
  const { state } = useAdminData();
  return state.orders.find((o) => o.id === id) ?? null;
}

export function usePublishedServices() { return useActiveServices(); }
export function useFeaturedPortfolio() { return useFeaturedItems(); }

export function useHomepageValue(key: string): Record<string, string> {
  const { state } = useAdminData();
  const found = state.homepageSections.find((h) => (h.id.includes(key) || h.section_key === key) && h.is_active);
  if (!found) return { zh: "", en: "", ja: "" };
  return { zh: found.title_zh, en: found.title_en, ja: found.title_ja };
}

export function useContentValue(_section: string, _key: string): Record<string, string> {
  return { zh: "", en: "", ja: "" };
}
